import { Injectable } from '@angular/core';
import { ToastController, ToastOptions } from '@ionic/angular';

/**
 * Toast Service
 * Centralized service for displaying toast notifications
 * Reduces code duplication across components
 */
@Injectable({
  providedIn: 'root'
})
export class ToastService {
  constructor(private toastController: ToastController) {}

  /**
   * Show an error toast message
   * @param message - Error message to display
   * @param duration - Duration in milliseconds (default: 3000)
   * @param position - Position on screen (default: 'bottom')
   * @returns Promise that resolves when toast is presented
   */
  async showError(
    message: string,
    duration: number = 3000,
    position: 'top' | 'bottom' | 'middle' = 'bottom'
  ): Promise<void> {
    const options: ToastOptions = {
      message,
      duration,
      position,
      mode: 'ios',
      color: 'danger',
      swipeGesture: 'vertical',
      buttons: [
        {
          text: 'OK',
          role: 'cancel'
        }
      ]
    };
    const toast = await this.toastController.create(options);
    await toast.present();
  }

  /**
   * Show a success toast message
   * @param message - Success message to display
   * @param duration - Duration in milliseconds (default: 3000)
   * @param position - Position on screen (default: 'bottom')
   * @returns Promise that resolves when toast is presented
   */
  async showSuccess(
    message: string,
    duration: number = 3000,
    position: 'top' | 'bottom' | 'middle' = 'bottom'
  ): Promise<void> {
    const options: ToastOptions = {
      message,
      duration,
      position,
      mode: 'ios',
      color: 'success',
      swipeGesture: 'vertical',
      buttons: [
        {
          text: 'OK',
          role: 'cancel'
        }
      ]
    };
    const toast = await this.toastController.create(options);
    await toast.present();
  }

  /**
   * Show an info toast message
   * @param message - Info message to display
   * @param duration - Duration in milliseconds (default: 3000)
   * @param position - Position on screen (default: 'bottom')
   * @returns Promise that resolves when toast is presented
   */
  async showInfo(
    message: string,
    duration: number = 3000,
    position: 'top' | 'bottom' | 'middle' = 'bottom'
  ): Promise<void> {
    const options: ToastOptions = {
      message,
      duration,
      position,
      mode: 'ios',
      color: 'primary',
      swipeGesture: 'vertical',
      buttons: [
        {
          text: 'OK',
          role: 'cancel'
        }
      ]
    };
    const toast = await this.toastController.create(options);
    await toast.present();
  }

  /**
   * Show a custom toast message
   * @param options - Custom toast options
   * @returns Promise that resolves when toast is presented
   */
  async showCustom(options: ToastOptions): Promise<void> {
    const toast = await this.toastController.create(options);
    await toast.present();
  }
}
